import React, { useState, useEffect } from 'react';
import styles from './UsersManagement.module.css';
import { getUsers } from '../services/apiUsers.ts';
import { getCustomers } from '../services/apiCustomers.ts';
import Spinner from './Spinner.tsx';
import Modal from './Modal.tsx';
import { useCreateUser } from '../hooks/useCreateUser.ts';
import { useEditUser } from '../hooks/useEditUser.ts';
import { useDeleteUser } from '../hooks/useDeleteUser.ts';
import { toast } from '../utils/sweetAlert.ts';
import { addUsersToCustomer } from '../services/apiCustomers.ts';
import { useCustomerUsers } from '../hooks/useCustomerUsers.ts';
import { useRemoveUserFromCustomer } from '../hooks/useRemoveUserFromCustomer.ts';
import { useUserDetails } from '../hooks/useUserDetails.ts';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
}

interface Customer {
  id: number;
  name: string;
  operator_number: string;
  water_source: string;
}

interface CustomerUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
}

interface UserCustomer {
  id: number;
  name: string;
  operator_number: string;
  water_source: string;
}

const UsersManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    roles: ['1'] // Manager role has ID 1
  });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState<boolean>(false);
  const [isViewCustomerUsersModalOpen, setIsViewCustomerUsersModalOpen] = useState<boolean>(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState<boolean>(false);
  const [selectedUserToRemove, setSelectedUserToRemove] = useState<string | null>(null);

  const { isCreatingUser, createUserAsync } = useCreateUser();
  const { isEditingUser, editUserAsync } = useEditUser();
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const { deleteUserAsync } = useDeleteUser(selectedUserId);
  const { customerUsers, isLoading: isLoadingCustomerUsers, refetch: refetchCustomerUsers } = useCustomerUsers(selectedCustomerId);
  const { isRemoving, removeUserFromCustomer } = useRemoveUserFromCustomer();
  const { userDetails, isLoading: isLoadingUserDetails, refetch: refetchUserDetails } = useUserDetails(currentUser?.id || null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersData, customersData] = await Promise.all([
        getUsers(),
        getCustomers()
      ]);
      setUsers(usersData);
      setCustomers(customersData);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenAddModal = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      roles: ['1'] // Manager role has ID 1
    });
    setIsAddModalOpen(true);
  };

  const handleOpenEditModal = (user: User) => {
    setCurrentUser(user);
    setFormData({
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      roles: user.roles
    });
    setIsEditModalOpen(true);
  };

  const handleOpenDeleteModal = (user: User) => {
    setCurrentUser(user);
    setSelectedUserId(user.id);
    setIsDeleteModalOpen(true);
  };

  const handleCloseModals = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsAssignModalOpen(false);
    setIsViewCustomerUsersModalOpen(false);
    setCurrentUser(null);
    setSelectedCustomerId(null);
    setSelectedCustomerName('');
    setSelectedUserToRemove(null);
  };

  const handleOpenAssignModal = (user: User) => {
    setCurrentUser(user);
    setIsAssignModalOpen(true);
    // Refetch user details when the modal is opened
    setTimeout(() => {
      if (user.id) {
        refetchUserDetails();
      }
    }, 100);
  };

  const handleOpenViewCustomerUsersModal = (customer: Customer) => {
    setSelectedCustomerId(customer.id);
    setSelectedCustomerName(customer.name);
    setIsViewCustomerUsersModalOpen(true);
  };

  const handleRemoveUserFromCustomer = async () => {
    if (!selectedCustomerId || !selectedUserToRemove) return;
    
    try {
      await removeUserFromCustomer({
        customerId: selectedCustomerId,
        userIds: [selectedUserToRemove]
      });
      
      // Refetch customer users to update the list
      refetchCustomerUsers();
      toast.success('User removed from customer successfully!');
      setSelectedUserToRemove(null);
    } catch (error: any) {
      toast.error(`Failed to remove user from customer: ${error.message}`);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({
      ...prev,
      roles: selectedOptions
    }));
  };

  const handleCustomerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCustomerId(Number(e.target.value));
  };

  const handleAssignToCustomer = async () => {
    if (!currentUser || !selectedCustomerId) {
      toast.error('Please select a customer');
      return;
    }

    try {
      setIsAssigning(true);
      await addUsersToCustomer(selectedCustomerId, [currentUser.id]);
      // Refetch user details to update the list of assigned customers
      refetchUserDetails();
      toast.success('User assigned to customer successfully!');
      // Don't close the modal so the user can see the updated list
      setSelectedCustomerId(null);
    } catch (error: any) {
      toast.error(`Failed to assign user to customer: ${error.message}`);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveFromCustomerInAssignModal = async (customerId: number) => {
    if (!currentUser) return;
    
    try {
      await removeUserFromCustomer({
        customerId,
        userIds: [currentUser.id]
      });
      
      // Refetch user details to update the list of assigned customers
      refetchUserDetails();
      toast.success('User removed from customer successfully!');
    } catch (error: any) {
      toast.error(`Failed to remove user from customer: ${error.message}`);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await createUserAsync({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        roles: formData.roles
      });
      
      await fetchData();
      handleCloseModals();
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !formData.firstName || !formData.lastName) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await editUserAsync({
        firstName: formData.firstName,
        lastName: formData.lastName,
        userId: currentUser.id,
        roles: formData.roles
      });
      
      await fetchData();
      handleCloseModals();
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleDeleteUser = async () => {
    if (!currentUser) return;
    
    try {
      await deleteUserAsync(currentUser.id);
      
      await fetchData();
      handleCloseModals();
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Users Management</h2>
        <div className={styles.headerButtons}>
          <button className={styles.addButton} onClick={handleOpenAddModal}>Add New User</button>
          <select 
            className={styles.customerSelect} 
            onChange={(e) => {
              const customerId = Number(e.target.value);
              if (customerId) {
                const customer = customers.find(c => c.id === customerId);
                if (customer) {
                  handleOpenViewCustomerUsersModal(customer);
                }
              }
            }}
            value=""
          >
            <option value="">View Users by Company</option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      ) : error ? (
        <div className={styles.errorMessage}>{error}</div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{`${user.first_name} ${user.last_name}`}</td>
                    <td>{user.email}</td>
                    <td>{user.roles.join(', ')}</td>
                    <td>
                      <div className={styles.actions}>
                        <button className={styles.editButton} onClick={() => handleOpenEditModal(user)}>Edit</button>
                        <button className={styles.deleteButton} onClick={() => handleOpenDeleteModal(user)}>Delete</button>
                        <button className={styles.assignButton} onClick={() => handleOpenAssignModal(user)}>Assign</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className={styles.pagination}>
            <p>Showing 1-{users.length} of {users.length} users</p>
          </div>

          {/* Add User Modal */}
          <Modal
            isOpen={isAddModalOpen}
            onClose={handleCloseModals}
            title="Add New User"
            footer={
              <div className={styles.modalFooter}>
                <button className={styles.cancelButton} onClick={handleCloseModals}>Cancel</button>
                <button className={styles.submitButton} onClick={handleAddUser} disabled={isCreatingUser}>
                  {isCreatingUser ? 'Adding...' : 'Add User'}
                </button>
              </div>
            }
          >
            <form className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="roles">Roles</label>
                <select
                  id="roles"
                  name="roles"
                  multiple
                  value={formData.roles}
                  onChange={handleRoleChange}
                >
                  <option value="1">Manager</option>
                  <option value="2">Viewer</option>
                  <option value="3">Editor</option>
                </select>
              </div>
            </form>
          </Modal>

          {/* Edit User Modal */}
          <Modal
            isOpen={isEditModalOpen}
            onClose={handleCloseModals}
            title="Edit User"
            footer={
              <div className={styles.modalFooter}>
                <button className={styles.cancelButton} onClick={handleCloseModals}>Cancel</button>
                <button className={styles.submitButton} onClick={handleEditUser} disabled={isEditingUser}>
                  {isEditingUser ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            }
          >
            <form className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="roles">Roles</label>
                <select
                  id="roles"
                  name="roles"
                  multiple
                  value={formData.roles}
                  onChange={handleRoleChange}
                >
                  <option value="1">Manager</option>
                  <option value="2">Viewer</option>
                  <option value="3">Editor</option>
                </select>
              </div>
            </form>
          </Modal>

          {/* Delete User Modal */}
          <Modal
            isOpen={isDeleteModalOpen}
            onClose={handleCloseModals}
            title="Delete User"
            footer={
              <div className={styles.modalFooter}>
                <button className={styles.cancelButton} onClick={handleCloseModals}>Cancel</button>
                <button className={styles.deleteButton} onClick={handleDeleteUser}>
                  Delete User
                </button>
              </div>
            }
          >
            <p>Are you sure you want to delete {currentUser?.first_name} {currentUser?.last_name}?</p>
            <p>This action cannot be undone.</p>
          </Modal>

          {/* Assign User to Customer Modal */}
          <Modal
            isOpen={isAssignModalOpen}
            onClose={handleCloseModals}
            title="Assign User to Customer"
            footer={
              <div className={styles.modalFooter}>
                <button className={styles.cancelButton} onClick={handleCloseModals}>Cancel</button>
                <button 
                  className={styles.submitButton} 
                  onClick={handleAssignToCustomer} 
                  disabled={isAssigning || !selectedCustomerId}
                >
                  {isAssigning ? 'Assigning...' : 'Assign User'}
                </button>
              </div>
            }
          >
            <div className={styles.form}>
              <p>Assign {currentUser?.first_name} {currentUser?.last_name} to a customer:</p>
              <div className={styles.formGroup}>
                <label htmlFor="customer">Select Customer</label>
                <select
                  id="customer"
                  name="customer"
                  value={selectedCustomerId || ''}
                  onChange={handleCustomerChange}
                  required
                >
                  <option value="">-- Select a Customer --</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Current Assigned Customers */}
              <div className={styles.currentCustomersSection}>
                <h4>Current Assigned Customers</h4>
                {isLoadingUserDetails ? (
                  <div className={styles.spinnerContainer}>
                    <Spinner />
                  </div>
                ) : userDetails && userDetails.customers && userDetails.customers.length > 0 ? (
                  <div className={styles.customersList}>
                    {userDetails.customers.map((customer: UserCustomer) => (
                      <div key={customer.id} className={styles.customerItem}>
                        <span>{customer.name}</span>
                        <button 
                          className={styles.removeButton}
                          onClick={() => handleRemoveFromCustomerInAssignModal(customer.id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No customers assigned to this user.</p>
                )}
              </div>
            </div>
          </Modal>

          {/* View Customer Users Modal */}
          <Modal
            isOpen={isViewCustomerUsersModalOpen}
            onClose={handleCloseModals}
            title={`Users Assigned to ${selectedCustomerName}`}
            footer={
              <div className={styles.modalFooter}>
                <button className={styles.cancelButton} onClick={handleCloseModals}>Close</button>
              </div>
            }
          >
            {isLoadingCustomerUsers ? (
              <div className={styles.spinnerContainer}>
                <Spinner />
              </div>
            ) : customerUsers && customerUsers.length > 0 ? (
              <div className={styles.customerUsersContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Roles</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerUsers.map((user: CustomerUser) => (
                      <tr key={user.id}>
                        <td>{`${user.first_name} ${user.last_name}`}</td>
                        <td>{user.email}</td>
                        <td>{user.roles.join(', ')}</td>
                        <td>
                          <button 
                            className={styles.removeButton} 
                            onClick={() => setSelectedUserToRemove(user.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No users assigned to this customer.</p>
            )}
          </Modal>

          {/* Confirm Remove User Modal */}
          {selectedUserToRemove && customerUsers && (
            <Modal
              isOpen={!!selectedUserToRemove}
              onClose={() => setSelectedUserToRemove(null)}
              title="Remove User from Company"
              footer={
                <div className={styles.modalFooter}>
                  <button 
                    className={styles.cancelButton} 
                    onClick={() => setSelectedUserToRemove(null)}
                  >
                    Cancel
                  </button>
                  <button 
                    className={styles.deleteButton} 
                    onClick={handleRemoveUserFromCustomer}
                    disabled={isRemoving}
                  >
                    {isRemoving ? 'Removing...' : 'Remove User'}
                  </button>
                </div>
              }
            >
              <p>
                Are you sure you want to remove {
                  customerUsers.find((user: CustomerUser) => user.id === selectedUserToRemove)?.first_name
                } {
                  customerUsers.find((user: CustomerUser) => user.id === selectedUserToRemove)?.last_name
                } from {selectedCustomerName}?
              </p>
            </Modal>
          )}
        </>
      )}
    </div>
  );
};

export default UsersManagement;
